'use client';
import Button from '@/components/Button';
import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { setSession } from '@/utils/tools';
import { QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import MembershipDescription from './membershipDescription';

export default function MemberLevel({
  memberLevelConfig,
  memberPrivileges,
  SDRewardsPageBtnClick,
}: {
  memberLevelConfig: any;
  memberPrivileges: any;
  SDRewardsPageBtnClick: (btnName: string) => void;
}) {
  const t = useTranslations('account.memberLevel');
  const userInfo = useStore((state) => state.userInfo);
  const router = useRouter();

  const [showScore, setShowScore] = useState(0);
  const [scoreProcess, setScoreProcess] = useState(0);
  const currency = useStore((state) => state.currency);

  const [config, setConfig] = useState({
    bgcolor: '',
    bgIcon: '',
  });

  const [levelInfo, setLevelInfo] = useState({
    name: '',
    spend: '',
    color: '',
    score: '',
    level_name: '',
  });

  const [nextLevelInfo, setNextLevelInfo] = useState({
    name: '',
    spend: '',
    color: '',
    score: '',
    level_name: '',
  });

  const [showMembershipDescription, setShowMembershipDescription] = useState(false);

  useEffect(() => {
    // setCurrentLevel(userInfo.member_level);
    const key = `v${userInfo.member_level}`;
    const currentConfig = memberLevelConfig[key] || memberLevelConfig.v0;
    const _levelInfo = memberPrivileges.level_info[userInfo.member_level];
    const _nextLevelInfo = memberPrivileges.level_info[userInfo.member_level + 1];

    setConfig(currentConfig);
    setLevelInfo(_levelInfo);
    setNextLevelInfo(_nextLevelInfo);
  }, [userInfo.member_level]);

  const handleShowNow = (groupID: string) => {
    SDRewardsPageBtnClick('Shop Now');
    setSession('zl_goodsListEnterPageScene', 'Zeelool Rewards');
    router.push(`/goods-list/${groupID}`);
  };

  useEffect(() => {
    if (userInfo.member_level < 3 && nextLevelInfo && levelInfo) {
      const nextLevelScore = Number(nextLevelInfo.score);
      const currentLevelScore = Number(levelInfo.score);
      const currentUserScore = Number(userInfo.member_score);
      setShowScore(nextLevelScore - currentUserScore);

      const calculatedProcess =
        ((currentUserScore - currentLevelScore) / (nextLevelScore - currentLevelScore)) * 100;
      setScoreProcess(Math.max(0, Math.min(100, calculatedProcess)));
    }
  }, [userInfo.member_level, nextLevelInfo, levelInfo]);

  return (
    <>
      <WebOnly>
        <div
          id="member-level"
          className={`w-[40.5%] h-[210px] rounded-[13px] px-24 py-28 flex flex-col justify-between relative ${config?.bgcolor}`}
        >
          <Image
            src={config?.bgIcon}
            width={176}
            height={90}
            alt="membership_price"
            className="absolute top-[13px] right-0 z-0"
          />
          <div className="header text-[#282828] flex justify-start items-center gap-10 z-11">
            <span className="font-bb text-[40px]">{levelInfo?.name}</span>
            <span className="font-br text-[20px] translate-y-[6px]">{levelInfo?.level_name}</span>

            <QuestionFilled
              className="text-[20px] cursor-pointer translate-y-[6px] opacity-40"
              onClick={() => router.push('/account/zeelool-rewards-desc')}
            />
          </div>
          <div className="mt-auto">
            {nextLevelInfo && (
              <>
                <p className="font-bb text-16">
                  {t('progressDescription.youAreJust')}{' '}
                  <span className="text-primary-purple">
                    {formatPriceNumberWithSymbol(showScore.toString(), currency, false, 0)}
                  </span>{' '}
                  {t('progressDescription.awayFromReaching')}{' '}
                  <span className="text-primary">{nextLevelInfo?.name}</span>
                </p>
                <div className="process bg-white rounded-[10px] h-5 mt-10">
                  <div
                    className="process-bar bg-black h-5 rounded-[10px]"
                    style={{ width: `${scoreProcess}%` }}
                  />
                </div>
              </>
            )}
            {userInfo.member_level > 0 && (
              <p className="mt-10 font-br text-14 opacity-50">
                {t('tierValidityDescription')}
                {/* @ts-ignore */}
                {userInfo?.member_expired_at || ''}
                ].
              </p>
            )}
          </div>
        </div>
      </WebOnly>
      <WapOnly>
        <div
          className={`w-full min-h-[147px] box-border  rounded-[13px] px-16 pt-20 pb-16 flex flex-col justify-between relative ${config?.bgcolor}`}
        >
          <Image
            src={config?.bgIcon}
            width={129}
            height={67}
            alt="membership_price"
            className="absolute top-[13px] right-0 z-0"
          />
          <div className="header text-[#282828] flex justify-start items-center gap-10 z-11">
            <span className="font-bb text-[28px]">{levelInfo?.name}</span>
            <span className="font-br text-[14px] translate-y-[4px]">{levelInfo?.level_name}</span>
            <QuestionOutlined
              className="text-[14px] translate-y-[4px]"
              onClick={() => setShowMembershipDescription(true)}
            />
            {showMembershipDescription && (
              <MembershipDescription
                userInfo={{ is_member: userInfo.is_member ?? 0 }}
                openDrawer={showMembershipDescription}
                onClose={() => setShowMembershipDescription(false)}
                closeDrawer={() => setShowMembershipDescription(false)}
              />
            )}
          </div>
          {nextLevelInfo && (
            <div className="mt-[40px]">
              <p className="font-bb text-14 leading-[17px]">
                {t('progressDescription.youAreJust')}{' '}
                <span className="text-primary-purple">
                  {formatPriceNumberWithSymbol(showScore.toString(), currency, false, 0)}
                </span>{' '}
                {t('progressDescription.awayFromReaching')}{' '}
                <span className="text-primary">{nextLevelInfo?.name}</span>
              </p>
              <div className="process bg-white rounded-[5px] h-[4px] mt-[8px]">
                <div
                  className="process-bar bg-[#282828] h-[4px] rounded-[5px]"
                  style={{ width: `${scoreProcess}%` }}
                />
              </div>
            </div>
          )}
          {userInfo.member_level > 0 && (
            <p className="mt-8 font-br text-12 opacity-50">
              {t('tierValidityDescription')}
              {userInfo?.member_expired_at || ''}
              ].
            </p>
          )}
        </div>

        <div className="w-full mt-20 flex justify-center mb-[53px]">
          <Button
            className="w-[240px] h-[36px] rounded-12 !bg-[#282828] text-white !text-14 !font-bb"
            variant="contained"
            color="black"
            radiusSize="large"
            onClick={() => handleShowNow(env.NEXT_PUBLIC_ZEELOOL_REWARDS_SHOP_NOW_BTN)}
          >
            {t('shopNow')}
          </Button>
        </div>
      </WapOnly>
    </>
  );
}
